import * as parser from './expression-parser';
import { parse as parseUtil } from 'pegjs-util';
import { print as printExpression } from './expression-printer';
import { uuidv4 } from '@graphistry/node-util/uuid';
import { filters } from './filters';
import { exclusions } from './exclusions';
import { histograms } from './histograms';
import { search } from './search';
import moment from 'moment';

export function expressions(view, options) {
    const defaultFilter = expression('LIMIT 800000');
    defaultFilter.expressionType = 'filter';

    return {
        // ...sets(workbookId, viewId),
        ...exclusions(view),
        ...histograms(view, options),
        ...search(view),
        ...filters(view, defaultFilter),
        expressionsById: {
            [defaultFilter.id]: {
                ...defaultFilter
            }
        }
    };
}

export { printExpression };

function getExprType(ast) {
    if (ast.argument) {
        return ast.argument;
    } else if (ast.left) {
        const left = getExprType(ast.left);
        if (left) return left;
        return getExprType(ast.right);
    } else if (ast.type === 'Identifier') {
        const parts = ast.name.split(':');
        const componentType = parts[0];
        const attribute = parts.slice(1).join(':');
        return { componentType, attribute, dataType: 'number' };
    }
}


function coerceUTCDatesInplace(ast) {
    if (!ast) {
        return;
    }
    if (ast.left) { coerceUTCDatesInplace(ast.left); }
    if (ast.right) { coerceUTCDatesInplace(ast.right); }
    if (ast.start) { coerceUTCDatesInplace(ast.start); }
    if (ast.stop) { coerceUTCDatesInplace(ast.stop); }
    if (ast.value) { coerceUTCDatesInplace(ast.value); }
    if (ast.argument) { coerceUTCDatesInplace(ast.argument); }
    if (ast.condition) { coerceUTCDatesInplace(ast.condition); }
    if (ast.result) { coerceUTCDatesInplace(ast.result); }
    if (ast.elseClause) { coerceUTCDatesInplace(ast.elseClause); }
    if (ast.cases && ast.cases instanceof Array) { ast.cases.map(coerceUTCDatesInplace); }
    if (ast.object) { coerceUTCDatesInplace(ast.object); }
    if (ast.property) { coerceUTCDatesInplace(ast.property); }
    if (ast.elements && ast.elements instanceof Array) { ast.elements.map(coerceUTCDatesInplace); }
    if (ast.arguments && ast.arguments instanceof Array) { ast.arguments.map(coerceUTCDatesInplace); }

    if (ast.type === 'Literal' && ast.value) {
        //Do not coerce "2000"
        //"2000-01-02".length == 10
        const mDate = moment(ast.value, moment.ISO_8601, true);
        if (ast.value.length >= 10 && mDate.isValid()) {
            ast.dataType = 'date';
            ast.value = mDate.toDate().toString(); //match default generator...
        }
    }
}

function coerceSingletonListsInplace(ast) {
    if (!ast) {
        return;
    }
    if (ast.left) { coerceSingletonListsInplace(ast.left); }
    if (ast.right) { coerceSingletonListsInplace(ast.right); }
    if (ast.start) { coerceSingletonListsInplace(ast.start); }
    if (ast.stop) { coerceSingletonListsInplace(ast.stop); }
    if (ast.value) { coerceSingletonListsInplace(ast.value); }
    if (ast.argument) { coerceSingletonListsInplace(ast.argument); }
    if (ast.condition) { coerceSingletonListsInplace(ast.condition); }
    if (ast.result) { coerceSingletonListsInplace(ast.result); }
    if (ast.elseClause) { coerceSingletonListsInplace(ast.elseClause); }
    if (ast.cases && ast.cases instanceof Array) { ast.cases.map(coerceSingletonListsInplace); }
    if (ast.object) { coerceSingletonListsInplace(ast.object); }
    if (ast.property) { coerceSingletonListsInplace(ast.property); }
    if (ast.elements && ast.elements instanceof Array) { ast.elements.map(coerceSingletonListsInplace); }
    if (ast.arguments && ast.arguments instanceof Array) { ast.arguments.map(coerceSingletonListsInplace); }

    if (ast.type === 'BinaryPredicate' && (ast.operator||'').toLowerCase() === 'in') {
        if (ast.right.type === 'Literal') {
            ast.right = {
                type: 'ListExpression',
                elements: [
                    ast.right
                ]
            }
        }
    }
}


export function expression(
    inputOrProps = {
        name: 'degree',
        dataType: 'number',
        componentType: 'point'
    },
    expressionId = uuidv4()
) {

    //console.debug({inputOrProps}, '@expression');

    let name = '',
        input = '',
        query = '',
        dataType = '',
        identifier = '',
        componentType = '';

    if (inputOrProps && typeof inputOrProps === 'string') {
        input = inputOrProps;

        query = parseUtil(parser, inputOrProps, { startRule: 'start' });
        coerceUTCDatesInplace(query.ast);
        coerceSingletonListsInplace(query.ast);

        if (query.error) {
            return { query };
        } else if (!query.ast) {
            return { query: { error: 'no ast' } };
        }

        const parts = getExprType(query.ast);
        if (parts) {
            componentType = parts.componentType;
            name = parts.attribute;
            dataType = parts.dataType;
            identifier = `${componentType}:${name}`;
        }
    } else if (inputOrProps && typeof inputOrProps === 'object') {
        name = inputOrProps.name || 'degree';
        dataType = inputOrProps.dataType || 'number';
        componentType = inputOrProps.componentType || 'point';
        identifier = `${componentType}:${name}`;
        query =
            inputOrProps.query ||
            getDefaultQueryForDataType({
                ...inputOrProps,
                name,
                dataType,
                identifier,
                componentType
            });
        input = printExpression(query);
        if (dataType === 'equals') {
            dataType = 'number';
        }
    }

    return {
        id: expressionId,
        enabled: true,
        level: undefined /* <-- 'system' | undefined */,
        readOnly: false,
        name,
        input,
        query,
        dataType,
        identifier,
        componentType /* 'edge' | 'point' */,
        expressionType: 'filter' /* <-- 'filter' | 'exclusion' */
    };
}

export function getDefaultQueryForDataType(queryProperties = {}) {

    //console.debug('getDefaultQueryForDataType for', queryProperties);    

    const { identifier, dataType = 'number', queryType = dataType } = queryProperties;


    //console.debug('===>qp2', { identifier, dataType, queryType }); 


    const queryFactory =
        defaultQueriesMap[queryType] || defaultQueriesMap[dataType] || defaultQueriesMap.literal;
    return {
        dataType,
        attribute: identifier,
        ...queryFactory.call(defaultQueriesMap, queryProperties)
    };
}

const defaultQueriesMap = {
    float(...args) {
        return this.number(...args);
    },
    integer(...args) {
        return this.number(...args);
    },
    number({ identifier, value = 0 }) {
        return {
            start: value,
            ast: {
                type: 'BinaryExpression',
                operator: '=',
                left: { type: 'Identifier', name: identifier },
                right: { type: 'Literal', value: value, dataType: 'number' }
            }
        };
    },
    categorical(...args) {
        return this.string(...args);
    },
    string({ identifier, value = 'ABC' }) {
        return {
            equals: value,
            ast: {
                type: 'BinaryExpression',
                operator: '=',
                left: { type: 'Identifier', name: identifier },
                right: { type: 'Literal', value: value, dataType: 'string' }
            }
        };
    },
    equals({ identifier, dataType, value = true }) {
        return {
            equals: value,
            ast: {
                type: 'BinaryPredicate',
                operator: '=',
                left: { type: 'Identifier', name: identifier, dataType },
                right: { type: 'Literal', value: value }
            }
        };
    },
    bool(args) {
        return defaultQueriesMap['boolean'](args);
    },
    boolean({ identifier, value = true }) {
        return {
            equals: value,
            ast: {
                type: 'BinaryPredicate',
                operator: 'IS',
                left: { type: 'Identifier', name: identifier },
                right: { type: 'Literal', value: value, dataType: 'boolean' }
            }
        };
    },
    datetime(...args) {
        return this.date(...args);
    },
    date({ identifier, value = 'now' }) {
        if (value === 'now') {
            value = new Date().getTime();
        }
        const strVal =
            value instanceof Date        ? value.toString()
            : typeof(value) === "string" ? value
            : typeof(value) === "number" ? (new Date(value)).toString()
            : null;
        return {
            ast: {
                type: 'BinaryExpression',
                operator: '=',
                left: { type: 'Identifier', name: identifier },
                right: { type: 'Literal', value: strVal, dataType: 'date' }
            }
        };
    },
    literal({ identifier, value = true, dataType }) {
        return {
            ast: {
                value,
                type: 'Literal',
                dataType
            }
        };
    },
    isOneOf({ identifier, values = [], dataType }) {
        if (!identifier) {
            throw new Error('isOneOf expected identifier: ' + identifier);
        }
        return {
            equals: values,
            ast: {
                type: 'BinaryPredicate',
                operator: 'IN',
                left: { type: 'Identifier', name: identifier },
                right: {
                    type: 'ListExpression',
                    elements: values.map(value => ({
                        value,
                        type: 'Literal',
                        dataType
                    }))
                }
            }
        };
    },
    isEqualTo({ identifier, value, dataType }) {
        switch (dataType) {
            case 'date':
            case 'datetime':
                return this.date({ identifier, value, dataType });
            default:
                return this.string({ identifier, value, dataType });
        }
    },
    isBetween({ identifier, start, stop, dataType }) {
        return {
            ast: {
                type: 'BetweenPredicate',
                value: { type: 'Identifier', name: identifier },
                start: { type: 'Literal', value: start, dataType },
                stop: { type: 'Literal', value: stop, dataType }
            }
        };
    }
};
